/**
 * Badge counts the user can trust.
 *
 * This browser holds a partial cache of the vault and knows nothing at all about
 * alerts or integration health until it asks. So a locally derived count is a guess: it
 * is shown, because a blank badge is worse, but it is labelled a guess, and an
 * authoritative snapshot from the server replaces it whole rather than being
 * merged field by field — a merged total matches neither side.
 */

import type { VaultNote } from "./types.js";

export interface Counts {
  readonly notes: number;
  readonly queued: number;
  readonly conflicts: number;
  readonly liveAlerts: number;
  readonly unhealthySources: number;
}

export type CountsOrigin = "authoritative" | "provisional";

export interface CountsSnapshot {
  readonly counts: Counts;
  readonly origin: CountsOrigin;
  /** When the server last spoke; absent while the counts are provisional. */
  readonly reconciledAt?: string;
}

export const EMPTY_COUNTS: Counts = {
  notes: 0,
  queued: 0,
  conflicts: 0,
  liveAlerts: 0,
  unhealthySources: 0,
};

/** What this device can work out on its own. Notes only; the rest it cannot see. */
export function derivedCounts(notes: readonly VaultNote[]): Counts {
  return {
    ...EMPTY_COUNTS,
    notes: notes.length,
    queued: notes.filter(({ state }) => state === "queued").length,
    conflicts: notes.filter(({ state }) => state === "conflict").length,
  };
}

function nonNegative(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 ? Math.floor(value) : undefined;
}

/**
 * Reads a `GET /v1/platform/counts` body. A field the server omits is not
 * invented, so a partial snapshot cannot silently zero a count the server never
 * claimed to know.
 */
export function parseServerCounts(payload: unknown): Partial<Counts> | undefined {
  if (typeof payload !== "object" || payload === null) return undefined;
  const record = payload as Record<string, unknown>;
  const entries = (Object.keys(EMPTY_COUNTS) as (keyof Counts)[])
    .map((key) => [key, nonNegative(record[key])] as const)
    .filter((entry): entry is readonly [keyof Counts, number] => entry[1] !== undefined);
  return entries.length === 0 ? undefined : Object.fromEntries(entries) as Partial<Counts>;
}

/**
 * The server's numbers win outright where it gave them. Where it stayed silent,
 * the local guess stands — and the snapshot is still marked authoritative,
 * because the server has spoken about the things it tracks.
 */
export function reconcileCounts(
  derived: Counts,
  server: Partial<Counts> | undefined,
  reconciledAt: string = new Date().toISOString(),
): CountsSnapshot {
  if (!server) return { counts: derived, origin: "provisional" };
  return { counts: { ...derived, ...server }, origin: "authoritative", reconciledAt };
}

/** One line telling the user how much to trust the badges beside it. */
export function countsLabel(snapshot: CountsSnapshot): string {
  return snapshot.origin === "authoritative"
    ? "Counts confirmed by the hosted vault."
    : "Counts from this device only — sync to confirm them against the hosted vault.";
}
